import { Link } from 'react-router-dom';
import { cloneElement, useId } from 'react';

/**
 * The small shared vocabulary. Kept deliberately minimal — Affily's visual
 * interest comes from typography and data density, not from component variety.
 */

const buttonBase =
  'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors ' +
  'disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2';

const variants = {
  primary: 'bg-accent text-white hover:bg-accent-hover',
  secondary: 'bg-ink text-paper hover:bg-ink/90',
  outline: 'border border-rule-strong bg-paper-raised text-ink hover:bg-paper-sunken',
  ghost: 'text-ink-muted hover:bg-paper-sunken hover:text-ink',
  danger: 'border border-negative/30 bg-negative-soft text-negative hover:bg-negative/10',
};

const sizes = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
};

export function Button({ variant = 'primary', size = 'md', className = '', as, ...props }) {
  const cls = `${buttonBase} ${variants[variant]} ${sizes[size]} ${className}`;
  if (props.to) return <Link className={cls} {...props} />;
  if (props.href) return <a className={cls} {...props} />;
  const Tag = as ?? 'button';
  return <Tag className={cls} {...props} />;
}

/**
 * A labelled form field.
 *
 * Three kinds of text, and the distinction matters:
 *
 *   - `constraint` — the RULE. What counts as valid. Always visible, including
 *     while an error is showing, because that is precisely the moment someone
 *     needs to know it. The previous version rendered the guidance only when
 *     there was no error, which hid it exactly when it was useful.
 *   - `hint` — explanatory prose. What the field is for.
 *   - `error` — what went wrong this time.
 */
export function Field({
  label,
  hint,
  constraint,
  error,
  required,
  children,
  className = '',
  id,
}) {
  // useId, not a module counter: a counter increments on every render, so the
  // id would change under the aria wiring it is supposed to anchor.
  const generated = useId();
  const fieldId = id ?? generated;
  const errorId = `${fieldId}-error`;
  const describedBy = [error ? errorId : null, constraint || hint ? `${fieldId}-help` : null]
    .filter(Boolean)
    .join(' ');

  return (
    <label className={`block ${className}`} htmlFor={fieldId}>
      <span className="mb-1.5 block text-sm font-medium text-ink">
        {label}
        {required && (
          <span className="ml-0.5 text-negative" aria-label="required">
            *
          </span>
        )}
      </span>

      {/* Children get the wiring without every caller having to pass it. */}
      {typeof children === 'object' && children?.type
        ? cloneElement(children, {
            id: fieldId,
            'aria-invalid': error ? true : undefined,
            'aria-describedby': describedBy || undefined,
            'aria-required': required || undefined,
          })
        : children}

      {error && (
        <span id={errorId} role="alert" className="mt-1 block text-xs font-medium text-negative">
          {error}
        </span>
      )}

      {(constraint || hint) && (
        <span id={`${fieldId}-help`} className="mt-1 block text-xs text-ink-faint">
          {constraint && <span className={error ? 'text-ink-muted' : ''}>{constraint}</span>}
          {constraint && hint && ' · '}
          {hint}
        </span>
      )}
    </label>
  );
}

const inputBase =
  'w-full rounded-md border border-rule-strong bg-paper-raised px-3 py-2 text-sm text-ink ' +
  'placeholder:text-ink-faint focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20';

export const Input = ({ className = '', ...props }) => (
  <input className={`${inputBase} ${className}`} {...props} />
);

export const Textarea = ({ className = '', ...props }) => (
  <textarea className={`${inputBase} min-h-24 resize-y ${className}`} {...props} />
);

export const Select = ({ className = '', ...props }) => (
  <select className={`${inputBase} ${className}`} {...props} />
);

/**
 * A short list of options as big boxes, side by side, instead of a dropdown:
 * every choice is visible and one tap away. Radio inputs underneath, so the
 * arrow keys and screen readers work as they do for any radio group.
 *
 * A fieldset, not a <Field>: Field is a <label>, and a label forwards clicks
 * to its first control, which would pick the first box whatever was clicked.
 *
 * `options`: `[{ value, title, description? }]`. `onChange` gets the input's
 * change event, like a <select>'s, so `set('key')` handlers work unchanged.
 */
export function ChoiceCards({
  label,
  hint,
  error,
  required,
  name,
  value,
  onChange,
  options,
  disabled = false,
  columns = 2,
  className = '',
}) {
  const generated = useId();
  const group = name ?? generated;
  const grid = { 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3', 4: 'grid-cols-2 sm:grid-cols-4' }[columns] ?? 'sm:grid-cols-2';

  return (
    <fieldset className={className} disabled={disabled} aria-invalid={error ? true : undefined}>
      <legend className="mb-1.5 text-sm font-medium text-ink">
        {label}
        {required && (
          <span className="ml-0.5 text-negative" aria-label="required">
            *
          </span>
        )}
      </legend>
      <div className={`grid gap-2 ${grid}`}>
        {options.map((o) => {
          const checked = value === o.value;
          return (
            <label
              key={o.value}
              className={`flex cursor-pointer flex-col rounded-lg border p-3 transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent/30 ${
                checked
                  ? 'border-accent bg-accent-soft'
                  : 'border-rule-strong bg-paper-raised hover:bg-paper-sunken'
              } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
            >
              <input
                type="radio"
                name={group}
                value={o.value}
                checked={checked}
                onChange={onChange}
                className="sr-only"
              />
              <span className="text-sm font-medium text-ink">{o.title}</span>
              {o.description && <span className="mt-0.5 text-xs text-ink-muted">{o.description}</span>}
            </label>
          );
        })}
      </div>
      {error && (
        <span role="alert" className="mt-1 block text-xs font-medium text-negative">
          {error}
        </span>
      )}
      {hint && <span className="mt-1 block text-xs text-ink-faint">{hint}</span>}
    </fieldset>
  );
}

export function Alert({ tone = 'negative', children, className = '' }) {
  const tones = {
    negative: 'border-negative/25 bg-negative-soft text-negative',
    positive: 'border-positive/25 bg-positive-soft text-positive',
    caution: 'border-caution/30 bg-caution-soft text-caution',
    info: 'border-accent/20 bg-accent-soft text-accent-ink',
  };
  if (!children) return null;
  return (
    <div role="alert" className={`rounded-md border px-3 py-2 text-sm ${tones[tone]} ${className}`}>
      {children}
    </div>
  );
}

export function Badge({ tone = 'neutral', children, className = '', title }) {
  const tones = {
    neutral: 'border-rule-strong bg-paper-sunken text-ink-muted',
    accent: 'border-accent/20 bg-accent-soft text-accent-ink',
    positive: 'border-positive/25 bg-positive-soft text-positive',
    negative: 'border-negative/25 bg-negative-soft text-negative',
    caution: 'border-caution/30 bg-caution-soft text-caution',
  };
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-2xs font-medium ${tones[tone]} ${className}`}
    >
      {children}
    </span>
  );
}

/**
 * A value the platform genuinely does not know, rendered as an em dash with a
 * reason. Used everywhere a metric has an empty denominator — this is the
 * visual counterpart to computeEpc() returning null instead of 0.
 */
export function Unknown({ reason = 'No data yet' }) {
  return (
    <span className="unknown" title={reason} aria-label={reason}>
      —
    </span>
  );
}

export function Card({ children, className = '' }) {
  return (
    <div
      className={`rounded-lg border border-rule bg-paper-raised shadow-card ${className}`}
    >
      {children}
    </div>
  );
}

export function EmptyState({ title, children, action }) {
  return (
    <div className="rounded-lg border border-dashed border-rule-strong px-6 py-12 text-center">
      <p className="font-medium text-ink">{title}</p>
      {children && <div className="mx-auto mt-1 max-w-md text-sm text-ink-muted">{children}</div>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

export function Spinner({ className = '' }) {
  return (
    <span
      role="status"
      aria-label="Loading"
      className={`inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    />
  );
}

/** Section heading used across dashboards and public pages. */
export function SectionTitle({ children, hint, action }) {
  return (
    <div className="mb-3 flex items-end justify-between gap-4">
      <div>
        <h2 className="text-sm font-semibold tracking-tight text-ink">{children}</h2>
        {hint && <p className="mt-0.5 text-xs text-ink-muted">{hint}</p>}
      </div>
      {action}
    </div>
  );
}
